"use client";

import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash } from "react-icons/pi";
import toast from "react-hot-toast";
import homepageApi from "@/services/homepageApi";
import CompaniesAnimated from "./CompaniesAnimated";

// Static Companies Section (Original V1)
function CompaniesStatic({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const companies = content.companies || [
        { name: "Google", logo: "https://logo.clearbit.com/google.com" },
        { name: "Microsoft", logo: "https://logo.clearbit.com/microsoft.com" },
        { name: "Amazon", logo: "https://logo.clearbit.com/amazon.com" },
        { name: "Netflix", logo: "https://logo.clearbit.com/netflix.com" },
    ];

    const addCompany = () => {
        const newCompanies = [...companies, { name: "New Company", logo: "https://placehold.co/100x40" }];
        onContentChange?.({ ...content, companies: newCompanies });
    };

    const removeCompany = (idx: number) => {
        const newCompanies = companies.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, companies: newCompanies });
    };

    const updateCompany = (idx: number, field: string, value: string) => {
        const newCompanies = [...companies];
        newCompanies[idx] = { ...newCompanies[idx], [field]: value };
        onContentChange?.({ ...content, companies: newCompanies });
    };

    return (
        <div
            className={`bg-white border-y border-gray-50 py-16 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ fontFamily }}
        >
            <div className="mx-auto max-w-7xl px-8 text-center">
                <p className="mb-10 text-sm font-semibold uppercase tracking-widest text-gray-500">
                    <EditableText
                        value={(content.title?.trim() ? content.title : null) || "Trusted by leading companies"}
                        onChange={(val) => onContentChange?.({ ...content, title: val })}
                        as="span"
                        placeholder="Section Title"
                    />
                </p>

                <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16 opacity-70 grayscale transition-all hover:grayscale-0 hover:opacity-100">
                    {companies.map((company: any, idx: number) => (
                        <div key={idx} className="relative group">
                            {isSelected && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); removeCompany(idx); }}
                                    className="absolute -top-3 -right-3 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-100 text-red-500"
                                    title="Remove"
                                >
                                    <PiTrash className="h-3 w-3" />
                                </button>
                            )}
                            <label className={`cursor-pointer block relative ${isSelected ? 'group-hover:scale-105 transition-transform' : ''}`}>
                                <img
                                    src={company.logo}
                                    alt={company.name}
                                    className={`h-8 md:h-10 w-auto object-contain ${isSelected ? 'group-hover:opacity-50 transition-opacity' : ''}`}
                                    onError={(e) => {
                                        e.currentTarget.src = "https://placehold.co/100x40?text=" + company.name;
                                    }}
                                />
                                {isSelected && (
                                    <>
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                            <span className="text-[9px] font-bold bg-white/90 text-gray-800 px-2 py-1 rounded shadow-sm whitespace-nowrap">
                                                Upload Logo
                                            </span>
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    const uploadPromise = homepageApi.uploadImage(file);
                                                    
                                                    toast.promise(uploadPromise, {
                                                        loading: "Uploading logo...",
                                                        success: "Logo uploaded successfully!",
                                                        error: "Failed to upload logo"
                                                    });

                                                    uploadPromise.then((response) => {
                                                        if (response.success && response.data?.url) {
                                                            updateCompany(idx, "logo", response.data.url);
                                                        } else {
                                                            toast.error(response.message || "Upload failed");
                                                        }
                                                    });
                                                }
                                            }}
                                        />
                                    </>
                                )}
                            </label>
                        </div>
                    ))}

                    {isSelected && (
                        <button
                            onClick={addCompany}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
                            title="Add Company"
                        >
                            <PiPlus className="h-5 w-5" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// Main Controller Component
export default function CompaniesSection(props: SectionContentProps) {
    const variant = props.content?.variant || "v1";

    switch (variant) {
        case "v2":
        case "companies-v2":
        case "animated":
        case "marquee":
            return <CompaniesAnimated {...props} />;
        case "v1":
        case "companies-v1":
        case "static":
        default:
            return <CompaniesStatic {...props} />;
    }
}
